import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../../constants';

// Типы для анализа стоимости
export interface RouteCostAnalysis {
  routeId: number;
  routeNumber: string;
  loadingAddress: string;
  unloadingAddress: string;
  totalAmount: number;
  totalWeight: number;
  totalVolume: number;
  costPerKg: number;
  costPerM3: number;
  date: string;
  status: string;
}

export interface DirectionCostSummary {
  direction: string;
  routeCount: number;
  totalAmount: number;
  totalWeight: number;
  totalVolume: number;
  averageCostPerKg: number;
  averageCostPerM3: number;
  minCostPerKg: number;
  maxCostPerKg: number;
  minCostPerM3: number;
  maxCostPerM3: number;
  routes: RouteCostAnalysis[];
}

export interface CostAnalysisResponse {
  summary: DirectionCostSummary[];
  totalRoutes: number;
  totalAmount: number;
  averageCostPerKg: number;
  averageCostPerM3: number;
  period: {
    from: string;
    to: string;
  };
}

export interface PopularDirection {
  direction: string;
  routeCount: number;
  loadingAddress: string;
  unloadingAddress: string;
}

export interface CostAnalysisParams {
  fromDate?: string;
  toDate?: string;
  city1?: string;
  city2?: string;
  minWeightKg?: number;
  maxWeightKg?: number;
  minVolumeM3?: number;
  maxVolumeM3?: number;
  groupByDirection?: boolean;
  limit?: number;
}

export const costAnalysisApi = createApi({
  reducerPath: 'costAnalysisApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_BASE_URL}`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['CostAnalysis'],
  endpoints: (builder) => ({
    getRouteCostAnalysis: builder.query<CostAnalysisResponse, CostAnalysisParams>({
      query: (params) => ({
        url: '/cost-analysis/route-costs',
        method: 'GET',
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          city1: params.city1,
          city2: params.city2,
          minWeightKg: params.minWeightKg,
          maxWeightKg: params.maxWeightKg,
          minVolumeM3: params.minVolumeM3,
          maxVolumeM3: params.maxVolumeM3,
          groupByDirection: params.groupByDirection,
          limit: params.limit,
          _ts: Date.now(),
        },
      }),
      providesTags: ['CostAnalysis'],
    }),

    getPopularDirections: builder.query<PopularDirection[], { limit?: number }>({
      query: (params) => ({
        url: '/cost-analysis/popular-directions',
        method: 'GET',
        params: {
          limit: params.limit,
          _ts: Date.now(),
        },
      }),
      providesTags: ['CostAnalysis'],
    }),

    getCities: builder.query<string[], void>({
      query: () => ({
        url: '/cost-analysis/cities',
        method: 'GET',
        params: { _ts: Date.now() },
      }),
      providesTags: ['CostAnalysis'],
    }),

    getCalculatorCostAnalysis: builder.query<CostAnalysisResponse, CostAnalysisParams>({
      query: (params) => ({
        url: '/cost-analysis/calculator-costs',
        method: 'GET',
        params: {
          fromDate: params.fromDate,
          toDate: params.toDate,
          city1: params.city1,
          city2: params.city2,
          minWeightKg: params.minWeightKg,
          maxWeightKg: params.maxWeightKg,
          minVolumeM3: params.minVolumeM3,
          maxVolumeM3: params.maxVolumeM3,
          groupByDirection: params.groupByDirection,
          limit: params.limit,
          _ts: Date.now(),
        },
      }),
      providesTags: ['CostAnalysis'],
    }),
  }),
});

export const {
  useGetRouteCostAnalysisQuery,
  useLazyGetRouteCostAnalysisQuery,
  useGetCalculatorCostAnalysisQuery,
  useLazyGetCalculatorCostAnalysisQuery,
  useGetPopularDirectionsQuery,
  useLazyGetPopularDirectionsQuery,
  useGetCitiesQuery,
  useLazyGetCitiesQuery,
} = costAnalysisApi;
